import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Circle, X, User, Heart, Calendar, MessageCircle, Cat, CheckSquare, ArrowRight, Bell, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerClose } from '@/components/ui/drawer';
import { 
  markOnboardingAsCompleted,
  resetOnboardingStatus,
  getChecklistStateKey
} from '@/utils/onboarding';
import { 
  getChecklistVisibility, 
  setChecklistVisibility, 
  markStepCompleted 
} from '@/utils/onboarding/onboardingEvents';

// Simple type for checklist items
type ChecklistItem = {
  id: string;
  title: string;
  description: string;
  isCompleted: boolean;
  path?: string;
};

// Default checklist items
const defaultChecklist: ChecklistItem[] = [
  {
    id: 'profile',
    title: 'Añade tus características físicas',
    description: 'Completa tu perfil para obtener recomendaciones personalizadas',
    isCompleted: false,
    path: '/nutricion'
  },
  {
    id: 'favorite',
    title: 'Añade una receta a tus Favoritos',
    description: 'Marca recetas que te gusten para acceder rápidamente a ellas',
    isCompleted: false,
    path: '/recetas'
  },
  {
    id: 'menu',
    title: 'Añade una receta a tu menú semanal',
    description: 'Planifica tus comidas para toda la semana de forma sencilla',
    isCompleted: false,
    path: '/menu-semanal'
  },
  {
    id: 'nutritionist',
    title: 'Habla con la nutricionista',
    description: 'Recibe asesoramiento personalizado durante todo tu camino',
    isCompleted: false,
    path: '/soporte'
  },
  {
    id: 'lisa',
    title: 'Habla con Lisa',
    description: 'Consulta a tu asistente de IA para resolver tus dudas sobre nutrición vegana',
    isCompleted: false,
    path: '/'
  },
  {
    id: 'notifications',
    title: 'Activa las notificaciones',
    description: 'Recibe consejos nutricionales y recordatorios directamente en tu dispositivo',
    isCompleted: false,
    path: '/notificaciones'
  },
  {
    id: 'tutorials',
    title: 'Ve los tutoriales',
    description: 'Aprende a usar todas las funciones de la app con nuestros tutoriales',
    isCompleted: false,
    path: '/tutoriales'
  }
];

const SimpleOnboardingChecklist: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Función para cargar el estado del checklist
  const loadChecklistState = () => {
    if (!user?.id) {
      setChecklist([...defaultChecklist]);
      return;
    }
    
    try {
      const checklistKey = getChecklistStateKey(user.id);
      const savedState = localStorage.getItem(checklistKey);
      
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        if (Array.isArray(parsedState) && parsedState.length === defaultChecklist.length) {
          console.log(`[ONBOARDING] Loaded saved state for user ${user.id}:`, parsedState);
          setChecklist(parsedState);
          return;
        }
      }
      
      console.log("[ONBOARDING] No valid saved state found, using default checklist");
      setChecklist([...defaultChecklist]);
    } catch (err) {
      console.error("[ONBOARDING] Error loading checklist state:", err);
      setChecklist([...defaultChecklist]);
    }
  };
  
  // Función para guardar el estado del checklist
  const saveChecklistState = (newChecklist: ChecklistItem[]) => {
    if (!user?.id || newChecklist.length === 0) return;
    
    try {
      const checklistKey = getChecklistStateKey(user.id);
      localStorage.setItem(checklistKey, JSON.stringify(newChecklist));
      console.log(`[ONBOARDING] Saved checklist state for user ${user.id}:`, newChecklist);
    } catch (err) {
      console.error("[ONBOARDING] Error saving checklist state:", err);
    }
  };
  
  // Función para actualizar un item específico
  const updateItem = (id: string) => {
    console.log(`[ONBOARDING] Updating item: ${id}`);
    
    setChecklist(prev => {
      const newChecklist = prev.map(item => 
        item.id === id ? { ...item, isCompleted: true } : item
      );
      
      // Guardar inmediatamente el nuevo estado
      saveChecklistState(newChecklist);
      
      return newChecklist;
    });
  };
  
  // Calculate progress
  const progress = checklist.length > 0 
    ? Math.round((checklist.filter(item => item.isCompleted).length / checklist.length) * 100)
    : 0;
  
  // Determine if all items are completed
  const allCompleted = progress === 100;
  
  // Check if user visits notifications page and mark as completed
  useEffect(() => {
    if (location.pathname === '/notificaciones' && user?.id && initialLoadComplete) {
      console.log("[ONBOARDING] User visited notifications page, marking as completed");
      updateItem('notifications');
    }
  }, [location.pathname, user?.id, initialLoadComplete]);
  
  // Check if user visits tutorials page and mark as completed
  useEffect(() => {
    if (location.pathname === '/tutoriales' && user?.id && initialLoadComplete) {
      console.log("[ONBOARDING] User visited tutorials page, marking as completed");
      updateItem('tutorials');
    }
  }, [location.pathname, user?.id, initialLoadComplete]);
  
  // Check if user visits support page and mark nutritionist as completed
  useEffect(() => {
    if (location.pathname === '/soporte' && user?.id && initialLoadComplete) {
      console.log("[ONBOARDING] User visited support page, marking nutritionist as completed");
      updateItem('nutritionist');
    }
  }, [location.pathname, user?.id, initialLoadComplete]);
  
  // Cuando cambia isOpen, actualizamos también el estado global
  useEffect(() => {
    if (initialLoadComplete) {
      setChecklistVisibility(isOpen);
    }
  }, [isOpen, initialLoadComplete]);
  
  // Load initial checklist state and auto-open if needed
  useEffect(() => {
    if (!user?.id) return;
    
    console.log(`[ONBOARDING] Loading initial state for user ${user.id}`);
    loadChecklistState();
    
    // Check if this is a new session (user just logged in)
    const sessionKey = `onboarding-session-${user.id}`;
    const hasShownThisSession = sessionStorage.getItem(sessionKey);
    
    // Auto-open checklist if:
    // 1. Haven't shown this session AND
    // 2. Onboarding is not completed AND
    // 3. Not on login/register pages
    const shouldAutoOpen = !hasShownThisSession && 
                           !allCompleted && 
                           !location.pathname.includes('/login') && 
                           !location.pathname.includes('/registro');
    
    if (shouldAutoOpen) {
      console.log("[ONBOARDING] Auto-opening checklist for new session");
      setIsOpen(true);
      // Mark that we've shown it this session
      sessionStorage.setItem(sessionKey, 'true');
    } else {
      // Load saved visibility preference
      const shouldBeOpen = getChecklistVisibility();
      setIsOpen(shouldBeOpen);
    }
    
    setInitialLoadComplete(true);
    
    // Escuchamos al evento de cambio de visibilidad del checklist
    const handleVisibilityChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const isVisible = customEvent.detail?.isOpen;
      if (typeof isVisible === 'boolean' && isVisible !== isOpen) {
        setIsOpen(isVisible);
      }
    };
    
    window.addEventListener('onboarding-visibility-changed', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('onboarding-visibility-changed', handleVisibilityChange);
    };
  }, [user?.id, location.pathname]);
  
  // Handle completion when all items are done
  useEffect(() => {
    if (!user?.id || checklist.length === 0 || !initialLoadComplete) return;
    
    if (allCompleted && !isComplete) {
      setIsComplete(true);
      console.log("[ONBOARDING] All items completed, marking onboarding as completed");
      
      // Update database status
      markOnboardingAsCompleted(user.id).then(() => {
        // Show completion toast
        toast({
          title: "¡Checklist completado!",
          description: "Has completado todos los pasos necesarios. ¡Enhorabuena!",
          duration: 5000,
        });
      });
    }
  }, [checklist, user?.id, allCompleted, isComplete, initialLoadComplete]);

  // Set up event listeners for completing checklist items
  useEffect(() => {
    if (!user || !initialLoadComplete) return;
    
    const handleProfileUpdate = () => updateItem('profile');
    const handleFavoriteAdd = () => updateItem('favorite');
    const handleMenuAdd = () => updateItem('menu');
    const handleNutritionistChat = () => updateItem('nutritionist');
    const handleLisaChat = () => updateItem('lisa');
    const handleNotificationsEnabled = () => updateItem('notifications');
    const handleTutorialsVisit = () => updateItem('tutorials');
    
    window.addEventListener('profile-updated', handleProfileUpdate);
    window.addEventListener('favorite-added', handleFavoriteAdd);
    document.addEventListener('favorite-added', handleFavoriteAdd);
    window.addEventListener('menu-added', handleMenuAdd);
    window.addEventListener('nutritionist-chat', handleNutritionistChat);
    window.addEventListener('lisa-chat', handleLisaChat);
    window.addEventListener('notifications-enabled', handleNotificationsEnabled);
    window.addEventListener('tutorials-visited', handleTutorialsVisit);
    
    return () => {
      window.removeEventListener('profile-updated', handleProfileUpdate);
      window.removeEventListener('favorite-added', handleFavoriteAdd);
      document.removeEventListener('favorite-added', handleFavoriteAdd);
      window.removeEventListener('menu-added', handleMenuAdd);
      window.removeEventListener('nutritionist-chat', handleNutritionistChat);
      window.removeEventListener('lisa-chat', handleLisaChat);
      window.removeEventListener('notifications-enabled', handleNotificationsEnabled);
      window.removeEventListener('tutorials-visited', handleTutorialsVisit);
    };
  }, [user, initialLoadComplete]);

  // Toggle completion of an item
  const toggleItemCompletion = (id: string) => {
    setChecklist(prev => {
      const newChecklist = prev.map(item => 
        item.id === id ? { ...item, isCompleted: !item.isCompleted } : item
      );
      
      // Guardar inmediatamente el nuevo estado
      saveChecklistState(newChecklist);
      
      return newChecklist;
    });
  };
  
  // Navigate to a route and close the drawer
  const handleNavigate = (path?: string) => {
    setIsOpen(false);
    if (path) {
      // Mark notifications item as completed when user clicks "Ver" for notifications
      if (path === '/notificaciones') {
        updateItem('notifications');
      }
      // Mark nutritionist item as completed when user clicks "Ver" for support
      if (path === '/soporte') {
        updateItem('nutritionist');
      }
      // Mark tutorials item as completed when user clicks "Ver" for tutorials
      if (path === '/tutoriales') {
        updateItem('tutorials');
      }
      navigate(path);
    }
  };
  
  // Handle close drawer and completion
  const handleCloseDrawer = async () => {
    setIsOpen(false);
    
    // If all tasks are completed when drawer is closed, mark as completed
    if (allCompleted && user?.id) {
      await markOnboardingAsCompleted(user.id);
    }
  };

  // If user isn't logged in, don't render anything
  if (!user) return null;
  
  // Count incomplete items
  const incompleteCount = checklist.filter(item => !item.isCompleted).length;

  return (
    <>
      {/* Button */}
      <div className="fixed bottom-4 left-4 z-40">
        <div className="relative">
          <Button 
            onClick={() => setIsOpen(true)} 
            className="rounded-full shadow-lg hover:shadow-xl transition-all bg-vegana-primary hover:bg-vegana-primary/90"
            size="icon"
          >
            <CheckSquare className="h-5 w-5" />
            {incompleteCount > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                {incompleteCount}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Drawer */}
      <Drawer open={isOpen} onOpenChange={handleCloseDrawer}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="border-b pb-4 mb-2">
            <div className="flex justify-between items-center">
              <DrawerTitle className="text-xl font-bold text-vegana-primary">
                Primeros Pasos
              </DrawerTitle>
              <DrawerClose onClick={handleCloseDrawer} className="p-1.5 rounded-full hover:bg-gray-100">
                <X className="h-5 w-5 text-gray-500" />
              </DrawerClose>
            </div>
            <DrawerDescription className="mt-2">
              Completa estas tareas para sacar el máximo partido a tu experiencia vegana
            </DrawerDescription>
            
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium">{progress}% completado</span>
                <span className="text-gray-500">
                  {checklist.filter(item => item.isCompleted).length}/{checklist.length} tareas
                </span>
              </div>
              <Progress value={progress} className="h-2 bg-gray-200" />
            </div>
          </DrawerHeader>
          
          <ScrollArea className="p-4 h-[50vh]">
            <ul className="space-y-3 px-4">
              {checklist.map((item) => (
                <motion.li 
                  key={item.id}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-3 rounded-lg border ${item.isCompleted ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <button 
                        onClick={() => toggleItemCompletion(item.id)}
                        className="focus:outline-none"
                      >
                        {item.isCompleted ? (
                          <CheckCircle className="h-6 w-6 text-green-600" />
                        ) : (
                          <Circle className="h-6 w-6 text-gray-300" />
                        )}
                      </button>
                    </div>
                    <div className="flex-grow">
                      <h3 className={`font-medium ${item.isCompleted ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                        {item.title}
                      </h3>
                      <p className={`text-sm mt-0.5 ${item.isCompleted ? 'text-gray-400' : 'text-gray-600'}`}>
                        {item.description}
                      </p>
                      {!item.isCompleted && item.path && (
                        <div className="mt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleNavigate(item.path)}
                            className="text-xs"
                          >
                            Ver <ArrowRight className="ml-1 h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {!item.isCompleted && (
                        <div className="p-1.5 rounded-full bg-gray-100 text-gray-500">
                          {item.id === 'profile' && <User className="h-5 w-5 text-vegana-primary" />}
                          {item.id === 'favorite' && <Heart className="h-5 w-5 text-vegana-primary" />}
                          {item.id === 'menu' && <Calendar className="h-5 w-5 text-vegana-primary" />}
                          {item.id === 'nutritionist' && <MessageCircle className="h-5 w-5 text-vegana-primary" />}
                          {item.id === 'lisa' && <Cat className="h-5 w-5 text-vegana-primary" />}
                          {item.id === 'notifications' && <Bell className="h-5 w-5 text-vegana-primary" />}
                          {item.id === 'tutorials' && <BookOpen className="h-5 w-5 text-vegana-primary" />}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.li>
              ))}
            </ul>
            
            {/* Si todos los items están completos, mostrar mensaje */}
            {allCompleted && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <div>
                    <h3 className="font-medium text-green-800">¡Has completado todas las tareas!</h3>
                    <p className="text-sm text-green-600">El checklist desaparecerá al cerrar esta ventana.</p>
                  </div>
                </div>
                <Button 
                  onClick={async () => {
                    if (user?.id) {
                      await markOnboardingAsCompleted(user.id);
                      handleCloseDrawer();
                    }
                  }}
                  className="w-full mt-3 bg-green-600 hover:bg-green-700"
                >
                  Entendido, no mostrar más
                </Button>
              </div>
            )}
            
            {/* Botón de reinicio solo para desarrollo */}
            <div className="mt-6 p-2 text-center border-t border-gray-200">
              <button 
                onClick={async () => {
                  if (user?.id) {
                    await resetOnboardingStatus(user.id);
                    setChecklist([...defaultChecklist]);
                    
                    toast({
                      title: "Checklist reiniciado",
                      description: "Se ha reiniciado el estado del checklist para pruebas.",
                    });
                  }
                }}
                className="text-xs text-gray-400 hover:text-gray-600"
              >
                Reiniciar checklist (solo desarrollo)
              </button>
            </div>
          </ScrollArea>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default SimpleOnboardingChecklist;
